import { AxiosError } from "axios";

import { useEffect } from "react";
import { SubmitHandler, useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import Button from "@/components/buttons/Button";
import CircleButton from "@/components/buttons/CircleButton";
import { IconCode, IconGoogle, IconQr } from "@/components/icons";
import Input from "@/components/inputs/Input";
import { MODAL_ROUTES } from "@/components/modals/router/ModalRoutes";

import { ROUTES } from "@/config/routes";
import { useGoogleSignin, usePostCheckUsername, usePostSendCode } from "@/hooks/api/login.hooks";
import useModalRouter from "@/hooks/useModalRouter";
import { formatUsername } from "@/utils/funcs";
import { onError } from "@/utils/onError";

import AuthTitle from "../AuthTitle.";
import { AuthSignInForm } from "../types";

const AuthLoginModal = () => {
  const { t } = useTranslation();
  const { mutate: mutateCheckUsername, isPending: isCheckUsernameLoading } = usePostCheckUsername();
  const { mutate: mutateSendCode, isPending: isSendCodeLoading } = usePostSendCode();
  const { mutate: mutateGoogleLogin } = useGoogleSignin();

  const isLoading = isCheckUsernameLoading || isSendCodeLoading;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
  } = useFormContext<AuthSignInForm>();

  const { openModal } = useModalRouter();

  const navigate = useNavigate();

  const handleCallbackResponse = (response: any) => {
    console.log(response);
    const formData = new FormData();
    formData.append("code", response.credential);

    mutateGoogleLogin(formData, {
      onSuccess: async (res) => {
        if (res.data.is_active) {
          const { setToken } = await import("@/api/tokens.ts");
          setToken(res.data.token);
          navigate(ROUTES.GATE.PROFILES);

          return;
        }

        openModal([MODAL_ROUTES.AUTH, MODAL_ROUTES.AUTH_SESSIONS], { token: res.data.token });
      },
      onError,
    });
  };

  const onSubmit: SubmitHandler<AuthSignInForm> = (data): void => {
    const username = formatUsername(data.username);
    setValue("username", username);
    mutateCheckUsername(
      { username },
      {
        onSuccess: (res) => {
          if (res.status === 200) {
            openModal([MODAL_ROUTES.AUTH, MODAL_ROUTES.AUTH_PASSWORD]);
          } else {
            mutateSendCode(username, {
              onSuccess: () => {
                openModal([MODAL_ROUTES.AUTH, MODAL_ROUTES.AUTH_OTP]);
              },
              onError: (error) => {
                const axiosError = error as AxiosError;
                if (axiosError.response && axiosError.response.data) {
                  const errorMessage = (axiosError.response.data as { message: string }).message;
                  setError("username", { message: errorMessage });
                } else {
                  setError("username", { message: t("error.something-wrong") });
                }
              },
            });
          }
        },
      },
    );
  };

  const openCodeLogin = () => {
    openModal([MODAL_ROUTES.AUTH, MODAL_ROUTES.AUTH_CODE]);
  };

  // eslint-disable-next-line
  function initClient() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    // eslint-disable-next-line
    google?.accounts?.id?.initialize({
      client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      callback: handleCallbackResponse,
    });
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    // eslint-disable-next-line
    google.accounts.id.renderButton(document.getElementById("signInIco"), {
      type: "icon",
      size: "large",
      shape: "circle",
      auto_prompt: "false",
    });
  }

  useEffect(() => {
    initClient();
  }, []);

  return (
    <>
      <AuthTitle title={t("Actions.sign-in-or-up")} subtitle={t("Actions.sign-in-or-up-desc")} />
      <div className={"mt-4 space-y-[1.875rem] transition-all max-lg:space-y-5"}>
        <form autoComplete="on" onSubmit={handleSubmit(onSubmit)} className={"space-y-6"}>
          <Input
            type={"text"}
            register={register}
            name={"username"}
            validation={{
              required: { value: true, message: t("Form.required") },
              pattern: {
                value: /^(?:[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+|\+?998\d{9})$/,
                message: t("incorrect-format"),
              },
            }}
            errors={errors}
            errorMessage={errors.username ? errors.username.message : ""}
            placeholder={t("Actions.input-email-or-phone-number")}
            autoComplete="off"
          />
          <div className={"space-y-4"}>
            <Button isLoading={isLoading} type={"submit"} aria-label={t("Actions.continue")} className={"uppercase"}>
              {t("Actions.continue")}
            </Button>
            <p className={" text-center text-on-surface"}>
              <Trans
                i18nKey="Actions.clicking-continue"
                components={{
                  1: <Link to={ROUTES.PRIVACY} className={"underline transition-colors hover:text-on-primary"} />,
                  2: <Link to={ROUTES.LICENSE} className={"underline transition-colors hover:text-on-primary"} />,
                }}
              />
            </p>
          </div>
        </form>
        <p
          className={
            "flex items-center gap-[0.9375rem] text-sm/[1.25rem] uppercase text-on-surface before:block before:h-px before:w-full before:bg-surface-container before:content-[''] after:block after:h-px after:w-full after:bg-surface-container after:content-['']"
          }
        >
          {t("General.or")}
        </p>
        <ul className={"flex items-center justify-center gap-5"}>
          <li className="relative">
            <CircleButton
              onClick={() => initClient()}
              icon={<IconGoogle />}
              title={t("Actions.enter-with-google")}
              buttonType={"entrance"}
            />
            <div
              id="signInIco"
              className="absolute left-1/2 top-1/2 translate-x-[-50%] translate-y-[-50%] scale-[1.2] opacity-[0.001]"
            ></div>
          </li>
          <li>
            <CircleButton
              title={t("Actions.enter-with-qr")}
              onClick={openCodeLogin}
              icon={<IconQr className={"fill-on-primary"} />}
              buttonType={"entrance"}
            />
          </li>
          <li>
            <CircleButton
              title={t("Actions.enter-with-code")}
              onClick={openCodeLogin}
              icon={<IconCode className={"fill-white max-lg:size-5"} />}
              buttonType={"entrance"}
            />
          </li>
        </ul>
      </div>
    </>
  );
};

export default AuthLoginModal;
